const piece = (slug, number, name, type, rest) => ({
  slug,
  number,
  name,
  type,
  image: `/products/${slug}.webp`,
  imageEx: `/products/${slug}-ex.webp`,
  cut: `/products/cut/${slug}.webp`,
  cutEx: `/products/cut/${slug}-ex.webp`,
  dimensions: 'Dimensions on request',
  ...rest,
});

export const DESCENT_PIECES = [
  piece('baton-styx', '01', 'Bâton Styx', 'Masterwork', {
    creed: 'An oath sworn on the river no god may break.',
    passage: ['The sceptre of the crossing—a trident cast in darkness', 'and set edge to edge in black stone.', 'The gods swore by the Styx because its word was final.', 'This is that word, given form: command that does not', 'need light to be obeyed.'],
    symbolism: [
      { icon: 'trident', text: 'The trident is dominion carried below—the same authority as the deep, stripped of its glow.' },
      { icon: 'meridian', text: 'The river line down the handle is the Styx: the boundary every oath must cross.' },
      { icon: 'light', text: 'Black pavé without visible metal: power that absorbs rather than reflects.' },
    ],
    closing: ['What is sworn on the Styx', 'is kept forever.'],
    crops: { detail: { fx: 0.5, fy: 0.16, z: 2.6 }, upper: { fx: 0.5, fy: 0.34, z: 1.8 }, lower: { fx: 0.5, fy: 0.8, z: 1.8 } },
    float: 'tall',
    ratio: 0.202,
    caption: ['An oath sworn on the river', 'no god may break.'],
  }),
  piece('the-abyss', '02', 'The Abyss', 'Ring', {
    creed: 'A void that pulls everything in. The centre is not seen—only felt.',
    passage: ['A spiral of black marquise stones turning inward', 'toward a centre that is never shown.', 'Every stone leans into the pull; nothing escapes the turn.', 'Worn as a reminder that depth is not a place—', 'it is a direction.'],
    symbolism: [
      { icon: 'wave', text: 'Spiral subduction and downward motion—the stones fold under one another as the void turns.' },
      { icon: 'ring', text: 'A vanishing centre and swallowed light: the eye is drawn to what is not there.' },
      { icon: 'anchor', text: 'Negative depth and gravitational pull—the weight that holds rather than lifts.' },
    ],
    closing: ['The centre is not seen.', 'Only felt.'],
    crops: {
      detail: { fx: 0.5, fy: 0.42, z: 2.3 },
      upper: { fx: 0.5, fy: 0.5, z: 1.15, src: '/products/abyss-side.webp', srcEx: '/products/abyss-side-ex.webp' },
      lower: { fx: 0.5, fy: 0.5, z: 1.15, src: '/products/abyss-back.webp', srcEx: '/products/abyss-back-ex.webp' },
    },
    float: 'tall',
    ratio: 1.016,
    caption: ['A void that pulls everything in.', 'The centre is not seen—only felt.'],
  }),
  piece('the-benediction', '03', 'The Benediction', 'Pendant', {
    creed: 'A blessing given from below.',
    passage: ['Black feathers of marquise light fold around a single', 'dark marquise—the blessing the abyss gives to those', 'who stayed long enough to receive it.', 'Not comfort. Clarity.'],
    symbolism: [
      { icon: 'marquise', text: 'The central marquise is the truth the descent leaves behind—whole, unlit, undeniable.' },
      { icon: 'tide', text: 'The feathered forms are the names kept in the dark—remembered, not mourned.' },
      { icon: 'ascend', text: 'The downward point: a blessing that arrives by descending, not by reaching up.' },
    ],
    closing: ['The dark does not take.', 'It gives what the light', 'could not.'],
    crops: { detail: { fx: 0.5, fy: 0.5, z: 2.6 }, upper: { fx: 0.5, fy: 0.16, ay: 0.3, z: 1.7 }, lower: { fx: 0.5, fy: 0.92, ay: 0.7, z: 1.7 } },
    float: 'tall',
    ratio: 0.412,
    caption: ['A blessing given', 'from below.'],
  }),
  piece('the-embrace', '04', 'The Embrace', 'Necklace', {
    creed: 'The hold of the dark around the throat.',
    passage: ['A collar of black marquise leaves closing around the neck,', 'a single drop turned downward toward the deep,', 'and the hallmark clasp that holds the circle shut.', 'The abyss does not seize. It embraces—', 'and does not let go.'],
    symbolism: [
      { icon: 'infinity', text: 'The closed collar is the embrace—unbroken, patient, absolute.' },
      { icon: 'bearing', text: 'The downward drop is a bearing to the deep: the way further down is always known.' },
      { icon: 'link', text: 'The hallmark clasp is the vow that keeps the circle—what the dark holds, it keeps.' },
    ],
    closing: ['What the dark holds,', 'it keeps.'],
    crops: { detail: { fx: 0.5, fy: 0.88, ay: 0.55, z: 2.6 }, upper: { fx: 0.5, fy: 0.08, ay: 0.4, z: 2.0 }, lower: { fx: 0.24, fy: 0.6, z: 1.8 } },
    float: 'wide',
    ratio: 0.924,
    caption: ['The hold of the dark', 'around the throat.'],
  }),
  piece('the-thorn-circle', '05', 'The Thorn Circle', 'Bracelet', {
    creed: 'A crown taken below, worn as a circle.',
    passage: ['A band of black thorn-leaf marquise closing around the wrist,', 'a larger thorn at the centre where a crown would rest its weight.', 'Protection that costs. A circle that cannot be broken', 'without the wearer’s consent.'],
    symbolism: [
      { icon: 'ring', text: 'The crown of thorns worn as a circle—sovereignty carried into the dark, not displayed above it.' },
      { icon: 'bearing', text: 'The thorn that guards, not wounds—every point turned outward, toward what would take from you.' },
      { icon: 'link', text: 'A circle that closes and holds—the clasp seals what the descent has made unbreakable.' },
    ],
    closing: ['What the thorns hold,', 'nothing takes.'],
    crops: { detail: { fx: 0.5, fy: 0.6, z: 2.4 }, upper: { fx: 0.5, fy: 0.32, ay: 0.45, z: 2.2 }, lower: { fx: 0.14, fy: 0.52, z: 2.0 } },
    float: 'wide',
    ratio: 2.056,
    caption: ['A crown taken below,', 'worn as a circle.'],
  }),
];

export const LEGACY_SLUGS = { abyss: 'the-abyss', benediction: 'the-benediction', embrace: 'the-embrace' };

export const DESCENT_ARTIFACTS = DESCENT_PIECES.map((p) => ({ slug: p.slug, name: p.name, type: p.type, caption: p.caption, image: p.image }));
export const DESCENT_ARTIFACTS_MORE = [];

export const DESCENT = {
  slug: 'the-descent',
  numeral: 'II',
  position: '02',
  name: 'The Descent',
  subtitle: 'The abyss remembered.',
  passage: [
    'Where light forgets to follow.\nWhere the forgotten names are kept.\nWhere truth is stripped of comfort.',
    'Ikarus walked here willingly.\nTo understand what humanity refuses to face.',
  ],
  sceneAlt: 'The Descent — Ikarus walking into the abyss',
  artifactsIntro: 'Forged in the silence.\nWitnesses to what lies beneath.',
  quote: 'The Descent does not corrupt. It reveals. Only what is real can survive it.',
  scene: '/realms/descent-scene.webp',
  sceneMobile: '/realms/descent-scene-m.webp',
  gold: '/realms/descent-gold.webp',
  goldMobile: '/realms/descent-gold-m.webp',
  sky: '/realms/descent-sky.webp',
  water: '/realms/descent-water.webp',
  extLeft: '/realms/descent-ext-left.webp',
  extRight: '/realms/descent-ext-right.webp',
  lineArt: '/realms/descent-art.webp',
  quoteArt: '/realms/descent-quote-left.webp',
  quoteArtRight: '/realms/descent-quote-right.webp',
  twinkles: [
    { x: 85.94, y: 30.21, size: 2.2, dur: 10, delay: 0.5 },
    { x: 17.48, y: 81.27, size: 1.2, dur: 12, delay: 4.2 },
    { x: 62.4, y: 41.5, size: 1.1, dur: 11, delay: 7.1 },
    { x: 74.8, y: 63.2, size: 1, dur: 13, delay: 2.6 },
    { x: 31.2, y: 47.8, size: 1, dur: 12.5, delay: 9.3 },
  ],
  mobileCrop: { x: 222, w: 580, srcW: 1024 },
};
